import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getBuyFeatureForm } from '../services/featureService';
import { logger } from '../utils/logger';
import './BuyFeaturePage.css';

const STORAGE_PREFIX = 'buyFeatureAllocations:';
const RESORT_DELAY_MS = 1500;
const FLIP_DURATION_MS = 480;
const FLIP_EASING = 'cubic-bezier(0.42, 0, 0.58, 1)';

function storageKey(slug) {
  return `${STORAGE_PREFIX}${slug}`;
}

function parsePoints(raw) {
  if (raw === '' || raw == null) return 0;
  const n = Number.parseInt(String(raw), 10);
  if (Number.isNaN(n)) return 0;
  return Math.max(0, Math.min(100, n));
}

/** Parsed intent without per-field 100 cap (for budget overflow message). */
function parsePointsLoose(raw) {
  if (raw === '' || raw == null) return 0;
  const n = Number.parseInt(String(raw), 10);
  if (Number.isNaN(n)) return 0;
  return Math.max(0, n);
}

/** Per-field points toward the 100 budget (cap 100 / feature so totals stay consistent). */
function pointsTowardBudget(raw) {
  return Math.min(100, parsePointsLoose(raw));
}

function sumAllocations(map, featureIds) {
  return featureIds.reduce((acc, id) => acc + pointsTowardBudget(map[id]), 0);
}

function sumPointsExcluding(map, featureIds, excludeId) {
  return featureIds
    .filter((id) => id !== excludeId)
    .reduce((acc, id) => acc + pointsTowardBudget(map[id]), 0);
}

function sortIdsByPoints(featureIds, vals, baseOrder) {
  return [...featureIds].sort((a, b) => {
    const va = pointsTowardBudget(vals[a]);
    const vb = pointsTowardBudget(vals[b]);
    if (vb !== va) return vb - va;
    return (baseOrder.get(a) ?? 0) - (baseOrder.get(b) ?? 0);
  });
}

const BuyFeaturePage = () => {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  const [values, setValues] = useState({});
  /** Display order; only changes after resort delay (not on every value change). */
  const [listOrder, setListOrder] = useState([]);
  const [lockedRowId, setLockedRowId] = useState(null);
  /** Budget overflow: show red row + round hint until OK (or focus clears). */
  const [overBudget, setOverBudget] = useState(null);
  const resortTimerRef = useRef(null);
  const baseOrderRef = useRef(new Map());
  const listRef = useRef(null);
  const flipFirstRectsRef = useRef(null);
  const inputRefMap = useRef(new Map());
  /** Latest values for blur handlers (avoid stale closures after OK / revert). */
  const valuesRef = useRef({});
  const overBudgetRef = useRef(null);
  /** Suppress one blur after OK (we already committed + started countdown). */
  const ignoreBlurFeatureIdRef = useRef(null);
  /** Points at last focus — blur must compare to this, not values state (onChange already synced state). */
  const focusBaselineRef = useRef(new Map());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!slug) return;
      setLoading(true);
      setError(null);
      try {
        const data = await getBuyFeatureForm(slug.trim());
        if (cancelled) return;
        if (!data) {
          setPayload(null);
          setListOrder([]);
          setError('This voting link is not valid or has been disabled.');
          setLoading(false);
          return;
        }
        if (!data.features || !data.features.length) {
          setPayload(data);
          setListOrder([]);
          setError('No features are tagged for this form yet.');
          setLoading(false);
          return;
        }
        setPayload(data);
        const ids = data.features.map((f) => f.id);
        const orderMap = new Map();
        data.features.forEach((f, i) => orderMap.set(f.id, i));
        baseOrderRef.current = orderMap;
        setListOrder(ids);

        let initial = {};
        try {
          const raw = localStorage.getItem(storageKey(slug));
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === 'object') {
              ids.forEach((id) => {
                if (Object.prototype.hasOwnProperty.call(parsed, id)) {
                  initial[id] = parsePoints(parsed[id]);
                }
              });
            }
          }
        } catch (e) {
          logger.warn('buy-feature localStorage read failed', e);
        }
        let sumPts = ids.reduce((acc, id) => acc + (initial[id] || 0), 0);
        if (sumPts > 100) {
          initial = {};
          try {
            localStorage.removeItem(storageKey(slug));
          } catch (e) {
            /* ignore */
          }
        }
        setValues(initial);
        setLockedRowId(null);
        setOverBudget(null);
        focusBaselineRef.current = new Map();
        setLoading(false);
      } catch (e) {
        if (!cancelled) {
          logger.error(e);
          setError('Could not load this form.');
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    return () => {
      if (resortTimerRef.current) {
        clearTimeout(resortTimerRef.current);
      }
    };
  }, []);

  const persist = useCallback(
    (next) => {
      setValues(next);
      if (!slug) return;
      try {
        localStorage.setItem(storageKey(slug), JSON.stringify(next));
      } catch (e) {
        logger.warn('buy-feature localStorage write failed', e);
      }
    },
    [slug]
  );

  const featureIds = useMemo(() => (payload?.features || []).map((f) => f.id), [payload]);
  useEffect(() => {
    valuesRef.current = values;
  }, [values]);
  useEffect(() => {
    overBudgetRef.current = overBudget;
  }, [overBudget]);
  const totalAllocated = useMemo(() => sumAllocations(values, featureIds), [values, featureIds]);

  const orderedFeatures = useMemo(() => {
    const byId = new Map((payload?.features || []).map((f) => [f.id, f]));
    return listOrder.map((id) => byId.get(id)).filter(Boolean);
  }, [payload, listOrder]);

  /** FLIP: after listOrder updates from a timed resort, slide rows to new positions. */
  useLayoutEffect(() => {
    const first = flipFirstRectsRef.current;
    if (!first || first.size === 0) return;
    flipFirstRectsRef.current = null;

    const container = listRef.current;
    if (!container) return;

    const invertedNodes = new Set();
    container.querySelectorAll('[data-feature-id]').forEach((node) => {
      const id = node.dataset.featureId;
      const r0 = first.get(id);
      if (!r0) return;
      const r1 = node.getBoundingClientRect();
      const dy = r0.top - r1.top;
      if (Math.abs(dy) < 0.5) return;
      invertedNodes.add(node);
      node.style.transition = 'none';
      node.style.transform = `translateY(${dy}px)`;
    });

    if (invertedNodes.size === 0) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        invertedNodes.forEach((node) => {
          node.style.transition = `transform ${FLIP_DURATION_MS}ms ${FLIP_EASING}`;
          node.style.transform = '';
        });
      });
    });
  }, [listOrder]);

  const startResortCountdown = useCallback(
    (featureId, nextVals) => {
      setLockedRowId(featureId);
      resortTimerRef.current = setTimeout(() => {
        resortTimerRef.current = null;
        const container = listRef.current;
        if (container) {
          const rectMap = new Map();
          container.querySelectorAll('[data-feature-id]').forEach((node) => {
            const id = node.dataset.featureId;
            rectMap.set(id, node.getBoundingClientRect());
          });
          flipFirstRectsRef.current = rectMap;
        } else {
          flipFirstRectsRef.current = null;
        }

        const newOrder = sortIdsByPoints(featureIds, nextVals, baseOrderRef.current);
        setListOrder(newOrder);
        setLockedRowId(null);
      }, RESORT_DELAY_MS);
    },
    [featureIds]
  );

  const applyPointsCommit = useCallback(
    (featureId, committed, nextValues, baselineFallback) => {
      persist(nextValues);
      setLockedRowId(null);
      if (resortTimerRef.current) {
        clearTimeout(resortTimerRef.current);
        resortTimerRef.current = null;
      }

      const atFocus = focusBaselineRef.current.has(featureId)
        ? focusBaselineRef.current.get(featureId)
        : baselineFallback;
      if (committed === atFocus) {
        return;
      }

      startResortCountdown(featureId, nextValues);
    },
    [persist, startResortCountdown]
  );

  const handleRoundOk = useCallback(() => {
    if (!overBudget) return;
    const { featureId, cap } = overBudget;
    const baselineFallback =
      focusBaselineRef.current.has(featureId)
        ? focusBaselineRef.current.get(featureId)
        : parsePoints(valuesRef.current[featureId]);
    setOverBudget(null);
    const next = { ...valuesRef.current, [featureId]: cap };
    ignoreBlurFeatureIdRef.current = featureId;
    applyPointsCommit(featureId, cap, next, baselineFallback);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        inputRefMap.current.get(featureId)?.blur();
      });
    });
  }, [overBudget, applyPointsCommit]);

  const handleBlur = useCallback(
    (featureId, rawString) => {
      if (ignoreBlurFeatureIdRef.current === featureId) {
        ignoreBlurFeatureIdRef.current = null;
        return;
      }

      const ob = overBudgetRef.current;
      if (ob && ob.featureId !== featureId) {
        const next = { ...valuesRef.current, [ob.featureId]: ob.cap };
        persist(next);
        setOverBudget(null);
      }

      if (resortTimerRef.current) {
        clearTimeout(resortTimerRef.current);
        resortTimerRef.current = null;
      }

      const vals = valuesRef.current;
      const prevInState = parsePoints(vals[featureId]);
      const sumOthers = sumPointsExcluding(vals, featureIds, featureId);
      const maxAllowed = Math.max(0, Math.min(100, 100 - sumOthers));
      const desiredLoose = parsePointsLoose(rawString);

      if (desiredLoose > maxAllowed) {
        setOverBudget({ featureId, entered: desiredLoose, cap: maxAllowed });
        return;
      }

      const v = desiredLoose;
      const next = { ...vals, [featureId]: v };
      const baselineFallback = focusBaselineRef.current.has(featureId)
        ? focusBaselineRef.current.get(featureId)
        : prevInState;
      applyPointsCommit(featureId, v, next, baselineFallback);
    },
    [featureIds, persist, applyPointsCommit]
  );

  if (loading) {
    return (
      <div className="buy-feature-page">
        <div className="buy-feature-inner">
          <p className="buy-feature-loading">Loading…</p>
        </div>
      </div>
    );
  }

  if (error && !payload?.features?.length) {
    return (
      <div className="buy-feature-page">
        <div className="buy-feature-inner">
          <h1 className="buy-feature-title">Buy-a-feature voting</h1>
          <p className="buy-feature-error">{error}</p>
          <p className="buy-feature-footnote">
            Inspired by the{' '}
            <a href="https://www.5dvision.com/post/using-buy-a-feature/" target="_blank" rel="noreferrer">
              Buy-a-Feature
            </a>{' '}
            prioritization exercise.
          </p>
        </div>
      </div>
    );
  }

  const { company_name: companyName, dimension_name: dimensionName, tag_name: tagName } = payload;

  return (
    <div className="buy-feature-page">
      <div className="buy-feature-inner">
        <header className="buy-feature-header">
          <h1 className="buy-feature-title">Allocate your points</h1>
          <p className="buy-feature-subtitle">
            {companyName ? <strong>{companyName}</strong> : 'Stakeholder'} — you have{' '}
            <strong>100 points</strong> total to spread across these features (0–100 per feature). This mirrors{' '}
            <a href="https://www.5dvision.com/post/using-buy-a-feature/" target="_blank" rel="noreferrer">
              Buy-a-Feature
            </a>
            : trade off what matters most.
          </p>
          {(dimensionName || tagName) && (
            <p className="buy-feature-meta">
              Template: {dimensionName && <span className="buy-feature-pill">{dimensionName}</span>}
              {dimensionName && tagName ? ' · ' : null}
              {tagName && <span className="buy-feature-pill">{tagName}</span>}
            </p>
          )}
        </header>

        <ul className="buy-feature-list" ref={listRef}>
          {orderedFeatures.map((f) => {
            const v = values[f.id] ?? '';
            const rowLocked = lockedRowId === f.id;
            const showOverBudget = overBudget?.featureId === f.id;
            return (
              <li
                key={f.id}
                className={`buy-feature-row${showOverBudget ? ' buy-feature-row-over' : ''}`}
                data-feature-id={f.id}
              >
                <div className="buy-feature-row-main">
                  <h2 className="buy-feature-name">{f.name}</h2>
                  {f.description ? <p className="buy-feature-desc">{f.description}</p> : null}
                </div>
                <div className="buy-feature-row-input">
                  <label htmlFor={`pts-${f.id}`}>Points</label>
                  <div className="buy-feature-input-slot">
                    <input
                      ref={(el) => {
                        if (el) inputRefMap.current.set(f.id, el);
                        else inputRefMap.current.delete(f.id);
                      }}
                      id={`pts-${f.id}`}
                      type="number"
                      min={0}
                      max={100}
                      inputMode="numeric"
                      value={v}
                      placeholder="0"
                      disabled={rowLocked}
                      onFocus={() => {
                        focusBaselineRef.current.set(f.id, parsePoints(valuesRef.current[f.id]));
                      }}
                      onChange={(e) => {
                        const rawInput = e.target.value === '' ? '' : e.target.value;
                        setValues((prev) => {
                          let raw = rawInput;
                          if (raw !== '') {
                            const sumOthers = sumPointsExcluding(prev, featureIds, f.id);
                            const maxHere = Math.max(0, Math.min(100, 100 - sumOthers));
                            const n = parsePointsLoose(raw);
                            if (n > maxHere) {
                              raw = maxHere === 0 ? '' : String(maxHere);
                            }
                          }
                          return { ...prev, [f.id]: raw };
                        });
                      }}
                      onBlur={(e) => handleBlur(f.id, e.target.value)}
                    />
                    {rowLocked ? (
                      <span className="buy-feature-row-spinner" aria-hidden>
                        <i className="fas fa-spinner fa-spin" />
                      </span>
                    ) : null}
                  </div>
                  {showOverBudget ? (
                    <div className="buy-feature-over-panel" role="region" aria-label="Point total limit">
                      <p className="buy-feature-over-text">
                        Round <strong>{overBudget.entered}</strong> to remaining <strong>{overBudget.cap}</strong>.
                      </p>
                      <button type="button" className="buy-feature-over-ok" onClick={handleRoundOk}>
                        OK
                      </button>
                    </div>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>

        <footer className="buy-feature-footer">
          <div className="buy-feature-total">
            <span>Points allocated</span>
            <strong>
              {totalAllocated} / 100
            </strong>
          </div>
          <p className="buy-feature-footnote">
            <Link to="/">Sign in to Featureview</Link> for the full feature map.
          </p>
        </footer>
      </div>
    </div>
  );
};

export default BuyFeaturePage;
